import 'view/css/overdue.css';

import moment from 'moment';
import React, { useState, useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons';

import PrintAdjustPanel from 'view/print/PrintAdjustPanel';
import PrintPreview from 'view/print/PrintPreview';

import API from 'service/api';
import Util, { Data } from 'service/util';
import Loading from 'component/Loading';
import Protected from 'component/Protected';
import LocationSelect from 'component/dropdown/LocationSelect';
import ModalScreen from 'component/ModalScreen';
import RecalcBillModal from 'component/bill/RecalcBillModal';
import LicenseListModal from 'component/bill/LicenseListModal';
import EInvoiceModal from 'component/e-invoice/EInvoiceModal';

class BillPageDTO
{
  constructor(bill)
  {
    for (let k of Object.keys(bill))
    {
      this[k] = bill[k]
    }

    this.max_ix = bill.ix_yrmo;
    this.bills  = [];
    this.due_lv = -1;
    this.D01    = '01';

    this.__show = true;
  }

  get total() {
    return Util.nf2(this.total_v);
  }
  get total_v() {
    return this.bills.reduce((r,b) => r + b.amount - b.discount, 0) - this.balance;
  }

  get yrmo() {
    return this.bills.map(b => moment(b.ix_yrmo, 'YYYYMM').format('YYYY-MM') ).join('\n');
  }
  get susp_mo() {
    let max_mo = Math.max.apply(null, this.bills.map(b => b.ix_yrmo));
    return moment(max_mo, 'YYYYMM').add(1, 'month').format('MM');
  }

  get signal() {
    let signal = [];
    if (!this.is_print_bill)  signal.push('NP');
    if (this.is_rtn_envelope) signal.push('M');
    if (this.is_whatsapp)     signal.push('W');
    if (this.is_email)        signal.push('E');
    if (this.is_mail)         signal.push('A');
    if (this.is_fax)          signal.push('F');
    if (this.cvc > 1)         signal.push(`V${this.cvc}`); // customer vehicle count
    return signal.join('/');
  }

}

const OverdueView = (props) =>
{
  const [exporting, setExporting] = useState(false);

  const [company, setCompany] = useState(Data.company[0].cd);
  const [license, setLicense] = useState('');
  const [isExact, setIsExact] = useState(true);
  const [filter_lv, setFilterLevel] = useState(0);

  const [printId, setPrintId] = useState(null);
  const [config,  setConfig]  = useState(null);

  // filter of bills sending methods
  const [filter_mode, setFilterMode] = useState('all');

  const [sel_dis, setDistrict] = useState(''); // mangement district code <string>
  const [sel_loc, setLocation] = useState(''); // location <object>

  const [print_addOn, setAddOn] = useState({ msg: '', x: 140, y: 53, fontSize: 5 });

  const [locMap,  setLocMap]      = useState(null);
  const [dict,    setDict]        = useState(null);
  const [sel_set, setSelectedSet] = useState([]);
  const [bills,   setBills]       = useState(null);

  const [modal, setModal]           = useState(null);
  const [modalTitle, setModalTitle] = useState('');

  //--------------------------------------------------
  // functions
  const refreshData = () => {
    API.getOverdueBills().then( data => {
      const today = moment().format('DD/MM/YYYY');

      let locMap = {};
      let all = new Map();
      data.forEach(b => {
        //-- obtain available location list
        if ( !(b.company in locMap) ) { locMap[b.company] = new Map(); }
        if (!locMap[b.company].get(b.srv_dis)) { locMap[b.company].set(b.srv_dis, new Set()); }
        locMap[b.company].get(b.srv_dis).add(b.srv_loc);

        //-- construct bill data
        let key = `${b.company}-${b.cd_license}`;
        if (!all.has(key)) {
          let o = new BillPageDTO(b);
          all.set(key, o);
          o.key = key;
          o.today = today;
        }

        // get the latest information
        let o = all.get(key);
        o.due_lv = Math.max(o.due_lv, b.due_lv);
        if (b.ix_yrmo > o.max_ix) {
          o.max_ix  = b.ix_yrmo;
          o.srv_dis = b.srv_dis;
          o.srv_loc = b.srv_loc;
          o.loc_key = b.loc_key;
          o.loc_psn = b.loc_psn;
        }

        // push to bill list
        o.bills.push(b);
      });

      // setup dictionary
      Object.keys(locMap).map(key_com => {
        for (let [dist, locs] of locMap[key_com])
          locMap[key_com].set(dist, Array.from(locs));
      });

      // move bills to filtered group (by company)
      let _dict = {};
      Array.from(all.values()).reduce((r,b) => {
        if (!(b.company in r)) r[b.company] = new Map();
        delete b.max_ix;
        r[b.company].set(b.cd_license, b);
        return r;
      }, _dict);

      setLocMap(locMap);
      setDict(_dict);
    });
  }

  const refreshFilter = () => {
    let filtered_bills = dict?.[company];

    if (!filtered_bills) {
      setBills([]);
      setSelectedSet([]);
      return;
    }

    // get values
    filtered_bills = Array.from(filtered_bills.values());

    // save unfiltered set
    setSelectedSet(filtered_bills);

    // madatory filter
    filtered_bills = filtered_bills.filter(b =>
      (b.total_v > 0         ) && // non-zero items only
      (b.due_lv == filter_lv ) && // selected bill type
      (b.__show              )    // go through the manual selection list
    );

    // filter by bill sending methods
    switch (filter_mode)
    {
      case 'all': break;
      case 'print-only':
        filtered_bills = filtered_bills.filter(b => !(b.is_whatsapp || b.is_email || b.is_fax));
        break;
      case 'm-only':
        filtered_bills = filtered_bills.filter(b => b.is_mail);
        break;
      case 'w-only':
        filtered_bills = filtered_bills.filter(b => b.is_whatsapp);
        break;
      case 'e-only':
        filtered_bills = filtered_bills.filter(b => b.is_email);
        break;
      case 'f-only':
        filtered_bills = filtered_bills.filter(b => b.is_fax);
        break;
    }

    // user simple filter
    if (license)
    {
      let filter_license = String(license).toUpperCase().trim();
      if (isExact)
        filtered_bills = filtered_bills.filter(b => b.cd_license === filter_license);
      else
        filtered_bills = filtered_bills.filter(b => -1<b.cd_license.toUpperCase().indexOf(filter_license));
    }
    if (sel_dis)
    {
      filtered_bills = !!sel_loc
        ? filtered_bills.filter(b => b.srv_dis == sel_loc.srv_dis && b.srv_loc == sel_loc.srv_loc)
        : filtered_bills = filtered_bills.filter(b => b.mgt_dis == sel_dis);
    }

    //----------------------------
    // sort
    let N2 = Math.ceil(filtered_bills.length / 2);
    let sortedIndex = [];
    for (let i = 0; i < filtered_bills.length; i++)
    {
      sortedIndex.push(i);
      sortedIndex.push(i + N2);
    }
    filtered_bills = filtered_bills.map((x,i) => filtered_bills[sortedIndex[i]]);

    // update list
    setBills(filtered_bills);
  }

  const exportXls = () =>
  {
    setExporting(true);
    API.exportOverdueBills().then(resp => {
      const ix_date = moment().format('YYYYMMDD');
      Util.download(resp, `逾期賬單_${ix_date}.xlsx`);
      setExporting(false);
    });
  }

  // modals
  const openRecalcModal = () => {
    setModalTitle('結算');
    setModal(<RecalcBillModal onUpdated={refreshData} closeModal={e=>setModal(null)} />);
  }
  const openListModal = () => {
    setModalTitle('選擇車輛');
    setModal(<LicenseListModal items={sel_set} />);
  }
  const open_eInvoiceModal = () => {
    setModalTitle('寄出電子賬單');
    setModal(<EInvoiceModal bills={dict} addOns={print_addOn} />);
  }

  const closeModal = () => {
    setModal(null);
    refreshFilter();
  }

  //------------------------------------------------------------
  // init
  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    let billType = (() => {
      switch (filter_lv) {
        case 0: return 'blue';
        case 1: return 'black';
        case 2:
        case 3: return 'red';
      }
    })();
    let printId = `invoice_${billType}`;
    setPrintId(printId);
    let config = Cache.Config.print[`${company}@${printId}`];
    setConfig(config);
    refreshFilter();
  }, [company, filter_lv]);

  useEffect(() => {
    refreshFilter();
  }, [dict, sel_dis, sel_loc, license, filter_mode]);

  //--------------------------------------------------
  // render
  return (<>
    <h1>日常操作 - 賬單及催款單</h1>
    <div className="action-panel">
      <div className="row overdue-search-bar">
        <label className="field">
          <strong>公司</strong>
          <select value={company} onChange={e=>setCompany(e.target.value)}>
            { Data.company.map(com =>
              <option key={com.cd} value={com.cd}>{com.cd} - {com.name}</option>
            )}
          </select>
        </label>
        <LocationSelect all={true} map={locMap?.[company]} by="management"
          onUpdate={(d, l, loc) => { setDistrict(d); setLocation(loc); }}
        />
        <label className="field">
          <strong>車牌</strong>
          <input value={license} onChange={e=>setLicense(e.target.value.toUpperCase())} />
        </label>
        <label className="field">
          <strong>完全符合</strong>
          <input type="checkbox" checked={isExact} onChange={e=>setIsExact(e.target.checked)} />
        </label>
      </div>
      <PrintAdjustPanel onChange={setAddOn}/>
    </div>
    <div className="action-panel">
      <div className="overdue-panel">
        <button className="btn dark-green small" onClick={openListModal}>
          <FontAwesomeIcon icon={faCheckCircle} />
        </button>
        <button className={`btn ${filter_lv==0?'blue':'white'}`} onClick={e=>setFilterLevel(0)}>收費單</button>
        <button className={`btn ${filter_lv==1?'blue':'white'}`} onClick={e=>setFilterLevel(1)}>逾期通知</button>
        <button className={`btn ${filter_lv==2?'blue':'white'}`} onClick={e=>setFilterLevel(2)}>最後通知</button>

        <label className="field-row">
          <strong>過濾模式</strong>
          <select value={filter_mode} onChange={e=>setFilterMode(e.target.value)}>
            <option value="all">全部</option>
            <option value="print-only">Print only</option>
            <option value="w-only">Whatsapp only</option>
            <option value="e-only">E-mail only</option>
            <option value="m-only">Mail only</option>
            <option value="f-only">Fax only</option>
          </select>
        </label>

        <Protected allow={['ADMIN','OPERATOR']}>
          <button className="btn blue small" onClick={open_eInvoiceModal}>電子賬單</button>
        </Protected>

      </div>
      <div className="button-group">
        <button className="btn red" onClick={e=>openRecalcModal()}>
          <span>結算</span>
        </button>
        <Protected allow={['ADMIN','OPERATOR','VIEWER_SU','VIEWER_DAY']}>
          <button className="btn dark-green btn-export" disabled={exporting}
            onClick={e=>exportXls()}>
            <FontAwesomeIcon icon={faFileExcel} />
            <span> 匯出</span>
          </button>
        </Protected>
      </div>
    </div>
    <div>
      { (null == bills)
        ? <Loading/>
        : (bills.length <= 0
          ? <p className="message yellow">沒有記錄</p>
          : <div className="overdue">
            <PrintPreview items={bills} company={company} printId={printId} config={config} addOn={print_addOn} />
          </div>
        )
      }
    </div>
    <ModalScreen show={!!modal}
      title={modalTitle}
      onClose={closeModal}>
      { modal }
    </ModalScreen>
  </>);

}

export default OverdueView;
